<script setup lang="ts">
import { onMounted, computed, onUnmounted, ref } from "vue";
import useAuthHelper from "@/composables/useAuthHelper";
import { useEventBus } from "@/eventBus";
import SiteBranding from "@/components/SiteBranding.vue";
import FeaturedIconSvg from "@/svgs/FeaturedIconSvg.vue";
import FileSvg from "@/svgs/FileSvg.vue";
import { useAuthStore } from "@/store/useAuthStore";
import { createToaster } from "@/toast";
import LocationSvg from "@/svgs/LocationSvg.vue";
import ArrowLeftSvg from "@/svgs/ArrowLeftSvg.vue";
import {logoutSession, switchAgency} from "@/services/auth";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const toast = createToaster();
const router = useRouter();
const isFlushLoading = ref(false);
const { redirectIfAuthenticated } = useAuthHelper();
const disabled = ref(true);
const { onEvent, offEvent } = useEventBus();
const getRouterLinkTo = (routeName) => {
    return disabled.value == true ? null : { name: routeName };
};
const agencies = computed(() => {
    return authStore?.user?.agencies??[];
});
const logOut = async () => {
    try {
        isFlushLoading.value = true;
        await logoutSession();
        localStorage.clear();
        await router.push({name: "login"});
    } catch (error) {
        toast.error("Error while flushing authenticated user data.");
    } finally {
        isFlushLoading.value = false;
    }
};
const back = async () => {
    if(agencies?.value?.length){
        if(agencies.value.length==1){
            const response = await switchAgency({agency_id:agencies.value[0].id});
            if (response) {
                window.location.href = '/dashboard';
            }
            window.location.reload();
        }else{
            return router.push({ name: "my-agencies" });
        }
    }else{
        logOut();
    }
};

onMounted(async () => {
    redirectIfAuthenticated();
    onEvent("enableLink", () => {
        disabled.value = false;
    });
});

onUnmounted(() => {
    offEvent("enableLink");
});
</script>

<template>
    <div class="min-h-screen flex h-auto p-4">
        <div
            class="max-w-[377px] lg:max-w-[476px] py-6 px-4 lg:p-8 w-full sticky top-8 rounded-xl bg-primary-10 hidden sm:block"
        >
            <SiteBranding main classes="max-w-[164px]" />
            <div
                class="mt-24 [&>*:not(:last-child)]:pb-8 [&>*:not(:last-child)]:before:content-[''] [&>*:not(:last-child)]:before:absolute [&>*:not(:last-child)]:before:top-[48px] [&>*:not(:last-child)]:before:h-[calc(100%_+_20px)] lg:[&>*:not(:last-child)]:before:h-full [&>*:not(:last-child)]:before:border-l [&>*:not(:last-child)]:before:border [&>*:not(:last-child)]:before:border-[rgb(var(--base-color-30))] [&>*:not(:last-child)]:before:w-[1px] [&>*:not(:last-child)]:before:left-[24px]"
            >
                <router-link
                    :to="{ name: 'agency-info' }"
                    @enable-link="disabled"
                    active-class="!opacity-100"
                    aria-current="step"
                    class="group relative overflow-hidden transition-all duration-300 ease-in-out flex items-center gap-4 opacity-30"
                >
                    <span
                        class="flex items-center rounded-lg bg-white shadow-[0px_1px_2px_0px_rgba(16,_24,_40,_0.05)]"
                        aria-hidden="true"
                    >
                        <span
                            class="relative z-10 flex h-12 w-12 items-center justify-center rounded-lg border border-base-30 bg-white"
                        >
                            <FeaturedIconSvg />
                        </span>
                    </span>
                    <span class="flex min-w-0 flex-col">
                        <span class="font-semibold text-base-100">
                            Website Setup
                        </span>
                        <span class="text-base-90">
                            Enter company name and subdomain
                        </span>
                    </span>
                </router-link>

                <component
                    :is="disabled ? 'span' : 'router-link'"
                    :to="{ name: 'website-info' }"
                    active-class="!opacity-100"
                    aria-current="step"
                    class="group relative overflow-hidden transition-all duration-300 ease-in-out flex items-center gap-4 opacity-30"
                    disabled
                >
                    <span
                        class="flex items-center shadow-[0px_1px_2px_0px_rgba(16,_24,_40,_0.05)]"
                        aria-hidden="true"
                    >
                        <span
                            class="relative z-10 flex h-12 w-12 items-center justify-center rounded-lg border border-base-30 bg-white"
                        >
                            <FileSvg />
                        </span>
                    </span>
                    <span class="flex min-w-0 flex-col">
                        <span class="font-semibold text-base-100">
                            Website Information
                        </span>
                        <span class="text-base-90">
                            A few details about your company
                        </span>
                    </span>
                </component>
                <!-- :class="disabled ? 'opacity-50': ''"
                :disabled="disabled" -->
                <component
                    :is="disabled ? 'span' : 'router-link'"
                    :to="{ name: 'agency-address' }"
                    active-class="!opacity-100"
                    aria-current="step"
                    class="group relative overflow-hidden flex transition-all duration-300 ease-in-out items-center gap-4 opacity-30"
                >
                    <span
                        class="flex items-center shadow-[0px_1px_2px_0px_rgba(16,_24,_40,_0.05)]"
                        aria-hidden="true"
                    >
                        <span
                            class="relative z-10 flex h-12 w-12 items-center justify-center rounded-lg border border-base-30 bg-white"
                        >
                            <LocationSvg />
                        </span>
                    </span>
                    <span class="flex min-w-0 flex-col">
                        <span class="font-semibold text-base-100">
                            Business Address
                        </span>
                        <span class="text-base-90">
                            Add your business details
                        </span>
                    </span>
                </component>
            </div>
            <span class="absolute right-0 bottom-0 z-[-1]">
                <svg
                    width="286"
                    height="326"
                    viewBox="0 0 286 326"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        opacity="0.04"
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M252.713 393.607C359.406 365.018 422.722 255.351 394.134 148.658C365.546 41.9643 255.878 -21.3522 149.185 7.23619C42.4919 35.8246 -20.8246 145.492 7.76381 252.185C36.3522 358.878 146.02 422.195 252.713 393.607ZM36.9294 248.759C36.5316 247.407 36.1493 246.046 35.7825 244.678C11.3405 153.459 65.4738 59.6969 156.693 35.2549C247.832 10.8343 341.509 64.8513 366.051 155.925L278.866 110.566L269.296 105.587L263.292 114.55L254.397 127.832L182.645 91.2314L172.911 86.2658L167.02 95.4699L124.096 162.539L110.722 155.607L101.047 150.593L95.0877 159.716L36.9294 248.759ZM47.6983 276.343L109.306 182.019L122.771 188.997L132.536 194.059L138.465 184.795L181.429 117.666L252.978 154.163L262.51 159.025L268.465 150.135L277.313 136.925L371.349 185.848C373.072 205.728 371.255 225.356 366.33 243.982L201.222 160.724L191.643 155.894L185.737 164.849L81.5328 322.844C67.915 309.586 56.3959 293.947 47.6983 276.343ZM100.078 338.5C108.329 344.52 117.103 349.794 126.285 354.255L204.816 235.95L210.74 227.025L220.297 231.863L343.817 294.379C349.507 285.72 354.41 276.554 358.455 267.002L199.949 187.073L100.078 338.5ZM328.926 313.854L218.971 258.203L149.142 363.399C158.714 366.452 168.59 368.673 178.663 370.001L222.947 303.545L228.837 294.706L238.346 299.44L307.761 334.004C315.419 327.875 322.492 321.126 328.926 313.854ZM284.797 349.492L237.112 325.749L206.722 371.355C219.467 370.913 232.361 369.029 245.205 365.588C259.345 361.799 272.594 356.345 284.797 349.492Z"
                        fill="black"
                    />
                </svg>
            </span>
            <div class="absolute left-6 bottom-4">
                <button class="flex items-center gap-2 p-2 font-medium text-sm hover:text-secondary-100 cursor-pointer" @click="back" :loading="isFlushLoading" :disabled="isFlushLoading">
                    <ArrowLeftSvg /> {{ agencies.length?'Back to Dashboard':'Back' }}
                </button>
            </div>
        </div>

        <div class="flex item-center flex-1">
            <div class="sm:px-8 py-12 max-w-[594px] w-full m-auto">
                <router-view />
            </div>
        </div>
    </div>
</template>
